const SAFE_BOOKING_DRAFT_MARKER_KEY = 'reach.bookingWizardDraftMarker.v3:phone'

const UNSAFE_BOOKING_DRAFT_PREFIXES = [
  'reach.bookingWizardDraft.v2',
  'reach.bookingWizardDraft:',
  'reach.bookingWizardDraft',
]

const BOOKING_DRAFT_MARKER_TTL_MS = 1000 * 60 * 60 * 24 * 3 // 3 days

type SafeBookingDraftMarker = {
  version: 3
  savedAt: number
  hasUnfinishedBooking: true
}

/**
 * Removes older unsafe draft keys that may have stored BookingData / PII.
 *
 * This intentionally removes broad legacy prefixes because older local testing
 * may have created scoped keys like:
 * reach.bookingWizardDraft.v2:phone:public
 *
 * Never store patient data in localStorage.
 */
export function clearUnsafeBookingDraftStorage() {
  if (typeof window === 'undefined') return

  try {
    const keysToRemove: string[] = []

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)

      if (!key) continue

      const isSafeMarker = key === SAFE_BOOKING_DRAFT_MARKER_KEY
      const isUnsafeBookingDraft = UNSAFE_BOOKING_DRAFT_PREFIXES.some(
        (prefix) => key.startsWith(prefix),
      )

      if (isUnsafeBookingDraft && !isSafeMarker) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key)
    })
  } catch {
    // Ignore storage cleanup failure.
  }
}

export function writeBookingDraftMarker() {
  if (typeof window === 'undefined') return

  clearUnsafeBookingDraftStorage()

  const marker: SafeBookingDraftMarker = {
    version: 3,
    savedAt: Date.now(),
    hasUnfinishedBooking: true,
  }

  try {
    window.localStorage.setItem(
      SAFE_BOOKING_DRAFT_MARKER_KEY,
      JSON.stringify(marker),
    )
  } catch {
    // Storage can fail in private mode / full storage.
    // Booking still works without the marker.
  }
}

export function hasValidBookingDraftMarker() {
  if (typeof window === 'undefined') return false

  clearUnsafeBookingDraftStorage()

  try {
    const raw = window.localStorage.getItem(SAFE_BOOKING_DRAFT_MARKER_KEY)

    if (!raw) return false

    const parsed = JSON.parse(raw) as Partial<SafeBookingDraftMarker>

    if (
      !parsed ||
      parsed.version !== 3 ||
      parsed.hasUnfinishedBooking !== true ||
      typeof parsed.savedAt !== 'number'
    ) {
      window.localStorage.removeItem(SAFE_BOOKING_DRAFT_MARKER_KEY)
      return false
    }

    if (Date.now() - parsed.savedAt > BOOKING_DRAFT_MARKER_TTL_MS) {
      window.localStorage.removeItem(SAFE_BOOKING_DRAFT_MARKER_KEY)
      return false
    }

    return true
  } catch {
    window.localStorage.removeItem(SAFE_BOOKING_DRAFT_MARKER_KEY)
    return false
  }
}

export function clearBookingDraftMarker() {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(SAFE_BOOKING_DRAFT_MARKER_KEY)
  } catch {
    // Ignore storage cleanup failure.
  }
}
